import React, { createContext, useState, useEffect } from "react";
import PropTypes from "prop-types";

/**
 * WebSocketContext is used to share the WebSocket instance across components.
 *
 * @type {React.Context<WebSocket|null>}
 */
export const WebSocketContext = createContext(null);

/**
 * WebSocketProvider component that establishes a WebSocket connection and provides it via context.
 *
 * @param {Object} props - The component props.
 * @param {string} props.url - The URL to establish the WebSocket connection.
 * @param {React.ReactNode} props.children - The child components that can access the WebSocket context.
 * @returns {JSX.Element} A provider component for the WebSocketContext.
 */
export function WebSocketProvider({ url, children }) {
	const [socket, setSocket] = useState(null);

	useEffect(() => {
		if (!url) return;

		const ws = new WebSocket(url);
		setSocket(ws);

		return () => {
			ws.close();
		};
	}, [url]);

	return (
		<WebSocketContext.Provider value={socket}>
			{children}
		</WebSocketContext.Provider>
	);
}

WebSocketProvider.propTypes = {
	url: PropTypes.string.isRequired,
	children: PropTypes.node.isRequired
};
